import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Building2, ChevronUp, LayoutGrid, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import Chip from '@/components/ui/Chip.tsx';
import { Separator } from '@/components/ui/Separator.tsx';
import { buttonVariants } from '@/components/buttons/actionButtonVariants.ts';
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/Drawer.tsx';
import {
  ScreenTabTileCompactContent,
  screenTabTileAutoGridClass,
  screenTabTileCompactActiveClass,
  screenTabTileCompactClass,
} from '@/components/ui/ScreenTabTile.tsx';
import { APP_MENU_SECTIONS } from '@/features/dashboard/_root/appMenu.ts';
import useActiveAppMenuItem from '@/features/dashboard/_root/hooks/useActiveAppMenuItem.ts';
import useSignOut from '@/features/auth/login/hooks/useSignOut.ts';
import useMe from '@/features/dashboard/_root/hooks/useMe.ts';
import MobileGlobalSearch, {
  MobileGlobalSearchField,
} from '@/features/dashboard/globalSearch/components/MobileGlobalSearch.tsx';

/**
 * Menu aplikacji na telefonie: pastylka nad dolną krawędzią z nazwą bieżącej
 * sekcji, która otwiera niski arkusz z kaflami wszystkich sekcji. Zastępuje
 * boczny sidebar w szufladzie — ten wymagał szukania hamburgera w nagłówku,
 * a pastylka jest zawsze pod kciukiem.
 */
const AppMobileMenu = () => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const active = useActiveAppMenuItem();
  const PillIcon = active?.icon ?? LayoutGrid;

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <DrawerTrigger asChild>
          <ActionButton
            tone="outline"
            size="md"
            aria-label="Otwórz menu aplikacji"
            className="pointer-events-auto rounded-full pl-4 pr-3 shadow-[0_8px_24px_rgba(46,46,56,0.18),0_2px_6px_rgba(46,46,56,0.08)]"
            // Arkusz ukrywa resztę strony przez `aria-hidden`; pastylka nie może
            // wtedy trzymać fokusu, bo przeglądarka zgłasza ukrycie aktywnego elementu.
            onClick={(event) => event.currentTarget.blur()}
          >
            <PillIcon aria-hidden className="text-accent-700" />
            <span className="font-extrabold tracking-[-0.01em]">
              {active?.label ?? 'Menu'}
            </span>
            <ChevronUp aria-hidden className="text-neutral-400" />
          </ActionButton>
        </DrawerTrigger>
      </div>

      <DrawerContent
        aria-describedby={undefined}
        className="rounded-t-[22px] border-t-0 bg-basic-white"
      >
        <DrawerTitle className="sr-only">Menu aplikacji</DrawerTitle>
        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3">
          <MobileGlobalSearchField
            onOpen={() => {
              // Arkusz schodzi, zanim wejdzie pełny ekran — dwie warstwy Radixa
              // naraz walczyłyby o fokus i blokadę przewijania.
              setOpen(false);
              setSearchOpen(true);
            }}
          />
          {APP_MENU_SECTIONS.map((section) => (
            <section key={section.label}>
              <h2 className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.09em] text-neutral-500">
                {section.label}
              </h2>
              <div className={screenTabTileAutoGridClass}>
                {section.items.map((item) => {
                  const isActive = item === active;
                  return (
                    <Link
                      key={item.label}
                      to={item.to}
                      onClick={() => setOpen(false)}
                      aria-current={isActive ? 'page' : undefined}
                      className={cn(
                        screenTabTileCompactClass,
                        isActive && screenTabTileCompactActiveClass,
                      )}
                    >
                      <ScreenTabTileCompactContent
                        icon={item.icon}
                        label={item.label}
                        active={isActive}
                      />
                    </Link>
                  );
                })}
              </div>
            </section>
          ))}

          <MobileMenuUser />
        </div>
      </DrawerContent>
      <MobileGlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </Drawer>
  );
};

const MobileMenuUser = () => {
  const { signOut } = useSignOut();
  const { data: me } = useMe();
  const invitationCount = me?.invitations.length ?? 0;

  return (
    <div className="flex items-center gap-2 border-t border-neutral-100 pt-3">
      <Link
        to="/auth/workspaces"
        className={cn(buttonVariants({ tone: 'ghost', size: 'sm' }))}
      >
        <Building2 aria-hidden />
        Firmy i zaproszenia
        {invitationCount > 0 && (
          <Chip label={String(invitationCount)} tone="accent" fill="soft" />
        )}
      </Link>
      <Separator orientation="vertical" className="h-5" />
      <ActionButton tone="ghost" size="sm" onClick={signOut}>
        <LogOut aria-hidden />
        Wyloguj się
      </ActionButton>
    </div>
  );
};

export default AppMobileMenu;
